/**
 * H2 additional-details pages (`buildPages`, `repeatDetail`): how a page's row is titled, and the
 * on-file record a row's picker holds.
 */
import { exposureContent } from "./exposure-content";
import { pickedOnFileItemFor, type RepeatItemAnswer } from "./exposure-answers";
import type { SectionOnFileSpec } from "./on-file-fields";
import { findOnFileScreen, onFileItemHeading, type OnFileRenderContext } from "./on-file-render";
import type { DetailsPageRow } from "./exposure-validation";
import type { OnFileScreenItem } from "../../state/on-file-port";

/** The record a picker field holds, looked up in the on-file read. */
export function pickedRecord(
  item: RepeatItemAnswer,
  onFileSpec: SectionOnFileSpec | undefined,
  onFile: OnFileRenderContext | undefined,
): OnFileScreenItem | undefined {
  const pickerSpec = onFileSpec?.picker;
  const picked = pickerSpec === undefined ? undefined : pickedOnFileItemFor(item, pickerSpec.fieldId);
  if (picked === undefined) {
    return undefined;
  }
  return findOnFileScreen(onFile, pickerSpec?.screenKey)?.items.find((candidate) => candidate.epicRecordId === picked.epicRecordId);
}

/** H2 additional-details row title: "New vehicle <n>", followed by the record a replacement replaces, or the parent row's
 * named field ("New building" while it is blank). */
export function additionalDetailsRowTitle(
  parent: DetailsPageRow,
  allRows: readonly RepeatItemAnswer[],
  onFileSpec: SectionOnFileSpec | undefined,
  onFile: OnFileRenderContext | undefined,
): string {
  const repeat = parent.question.repeat;
  const page = repeat?.additionalDetailsPage;
  const triggers = repeat?.additionalDetailsTriggerOutcomes ?? [];
  if (page?.headerNoun !== undefined) {
    const ordinal = allRows.slice(0, parent.rowNumber).filter((row) => triggers.includes(row.outcome)).length;
    let replacing = "";
    if (parent.row.outcome === "Replaced") {
      const record = pickedRecord(parent.row, onFileSpec, onFile);
      replacing = record === undefined ? "" : onFileItemHeading(record, onFileSpec?.picker?.labelAttributes ?? []);
    }
    return exposureContent.detailsNewItemTitle(page.headerNoun, ordinal, replacing);
  }
  const named = page?.headerField === undefined ? "" : (parent.row.fields[page.headerField] ?? "").trim();
  if (named !== "") {
    return named;
  }
  return page?.headerFallback ?? exposureContent.rowTitle(repeat?.itemLabel ?? "", parent.rowNumber, repeat?.noTitle === true);
}

